/**
 * Assembly Information Widget
 * Displays assembly difficulty information and offers professional assembly service
 *
 * Features:
 * - Detects assembly tier from product tags (Assembly-Tier-1, Assembly-Tier-2, Assembly-Tier-3)
 * - Maps tier to difficulty level (Easy, Moderate, Complex)
 * - Displays difficulty badge, star rating, estimated time, and required tools
 * - Fetches assembly service addon from bundle-config.json
 * - Integrates with Shopify product API for real-time pricing
 * - Calculates discounted prices based on current bundle tier
 * - Add to cart functionality with cart refresh
 * - x-modal pattern for detailed view (matching Santa Video widget)
 * - British English throughout
 * - All SVG icons inline
 * - Theme token styling
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class AssemblyInfoWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.tierNumber = null;
      this.difficultyData = null;
      this.assemblyAddon = null;
      this.bundleConfig = null;
      this.productData = null;
      this.variantId = null;
      this.modalId = `assembly-info-modal-${config.id}`;

      // Content settings with defaults
      this.content = {
        // Widget card
        cardHeading: config.cardHeading || 'Assembly Information',
        cardSubheadingFormat: config.cardSubheadingFormat || '{level} assembly required',
        serviceAvailableText: config.serviceAvailableText || 'Professional assembly service available',
        viewDetailsButton: config.viewDetailsButton || 'View Full Assembly Details',

        // Tier 1 - Easy
        tier1Level: config.tier1Level || 'Easy',
        tier1Badge: config.tier1Badge || 'Quick & Easy Assembly',
        tier1QuickDescription: config.tier1QuickDescription || 'Minimal assembly - perfect for quick setup',

        // Tier 2 - Moderate
        tier2Level: config.tier2Level || 'Moderate',
        tier2Badge: config.tier2Badge || 'Moderate Assembly Required',
        tier2QuickDescription: config.tier2QuickDescription || 'Moderate assembly - allow adequate time',

        // Tier 3 - Complex
        tier3Level: config.tier3Level || 'Complex',
        tier3Badge: config.tier3Badge || 'Complex Assembly - Consider Our Service',
        tier3QuickDescription: config.tier3QuickDescription || 'Complex assembly - professional service recommended',

        // Tools - Tier 1
        tier1Tool1: config.tier1Tool1 || 'Basic screwdriver',
        tier1Tool2: config.tier1Tool2 || 'Included allen keys',

        // Tools - Tier 2
        tier2Tool1: config.tier2Tool1 || 'Screwdriver set',
        tier2Tool2: config.tier2Tool2 || 'Allen keys',
        tier2Tool3: config.tier2Tool3 || 'Spanner',
        tier2Tool4: config.tier2Tool4 || 'Drill (optional)',

        // Tools - Tier 3
        tier3Tool1: config.tier3Tool1 || 'Full toolkit',
        tier3Tool2: config.tier3Tool2 || 'Drill (required)',
        tier3Tool3: config.tier3Tool3 || 'Spanner set',
        tier3Tool4: config.tier3Tool4 || '2 people recommended',

        // Steps - Tier 1
        tier1Step1: config.tier1Step1 || 'Attach wheels to chassis',
        tier1Step2: config.tier1Step2 || 'Connect steering wheel',
        tier1Step3: config.tier1Step3 || 'Install battery and secure panel',
        tier1Step4: config.tier1Step4 || 'Test controls before first use',

        // Steps - Tier 2
        tier2Step1: config.tier2Step1 || 'Assemble main chassis components',
        tier2Step2: config.tier2Step2 || 'Install wheels and suspension',
        tier2Step3: config.tier2Step3 || 'Connect electronics and wiring',
        tier2Step4: config.tier2Step4 || 'Attach steering and controls',
        tier2Step5: config.tier2Step5 || 'Install battery and secure panels',
        tier2Step6: config.tier2Step6 || 'Full safety check and testing',

        // Steps - Tier 3
        tier3Step1: config.tier3Step1 || 'Pre-sort and organise all components',
        tier3Step2: config.tier3Step2 || 'Assemble complex chassis structure',
        tier3Step3: config.tier3Step3 || 'Install advanced suspension system',
        tier3Step4: config.tier3Step4 || 'Connect complex wiring harness',
        tier3Step5: config.tier3Step5 || 'Install electronic control units',
        tier3Step6: config.tier3Step6 || 'Attach body panels and accessories',
        tier3Step7: config.tier3Step7 || 'Install battery system',
        tier3Step8: config.tier3Step8 || 'Comprehensive safety testing',
        tier3Step9: config.tier3Step9 || 'Final quality inspection',

        // Benefits
        benefit1: config.benefit1 || 'Delivered fully assembled and ready to ride',
        benefit2: config.benefit2 || 'Professional safety checks completed',
        benefit3: config.benefit3 || 'All electronics tested and verified',
        benefit4: config.benefit4 || 'Save time and avoid frustration',
        benefit5: config.benefit5 || 'Perfect for the big reveal day',

        // Modal content
        modalTitle: config.modalTitle || 'Assembly Information',
        modalHeroHeadingFormat: config.modalHeroHeadingFormat || '{level} Assembly Required',
        modalTimeFormat: config.modalTimeFormat || 'Estimated Time: {min}-{max} minutes',
        modalToolsHeading: config.modalToolsHeading || 'Tools Required:',
        modalOptionalLabel: config.modalOptionalLabel || '(optional)',
        modalStepsHeading: config.modalStepsHeading || 'Assembly Steps Overview:',
        modalServiceHeading: config.modalServiceHeading || 'Professional Assembly Service Available',
        modalSavingsFormat: config.modalSavingsFormat || 'Save {amount} ({percent}% off)',
        modalNoticeText: config.modalNoticeText || 'Assembly times are estimates based on average experience. Actual time may vary depending on skill level and tools available.',

        // Button labels & states
        buttonAddService: config.buttonAddService || 'Add Assembly Service to Cart',
        buttonAdding: config.buttonAdding || 'Adding to Cart...',
        buttonAdded: config.buttonAdded || 'Added to Cart!',
        buttonUnavailable: config.buttonUnavailable || 'Service Unavailable',

        // Alerts
        alertUnableToAdd: config.alertUnableToAdd || 'Unable to add assembly service. Please refresh the page and try again.'
      };

      // Difficulty mapping by tier (using customisable content)
      this.difficultyMapping = {
        1: {
          level: this.content.tier1Level,
          stars: 2,
          timeMin: 30,
          timeMax: 45,
          colour: 'green',
          colourRGB: '34, 197, 94',
          badge: this.content.tier1Badge
        },
        2: {
          level: this.content.tier2Level,
          stars: 3,
          timeMin: 45,
          timeMax: 90,
          colour: 'amber',
          colourRGB: '251, 191, 36',
          badge: this.content.tier2Badge
        },
        3: {
          level: this.content.tier3Level,
          stars: 4,
          timeMin: 90,
          timeMax: 120,
          colour: 'red',
          colourRGB: '239, 68, 68',
          badge: this.content.tier3Badge
        }
      };

      // Tools lists by tier (using customisable content)
      this.toolsMapping = {
        1: [
          { name: this.content.tier1Tool1, icon: 'screwdriver', required: true },
          { name: this.content.tier1Tool2, icon: 'tool', required: true }
        ],
        2: [
          { name: this.content.tier2Tool1, icon: 'screwdriver', required: true },
          { name: this.content.tier2Tool2, icon: 'tool', required: true },
          { name: this.content.tier2Tool3, icon: 'spanner', required: true },
          { name: this.content.tier2Tool4, icon: 'drill', required: false }
        ],
        3: [
          { name: this.content.tier3Tool1, icon: 'tool', required: true },
          { name: this.content.tier3Tool2, icon: 'drill', required: true },
          { name: this.content.tier3Tool3, icon: 'spanner', required: true },
          { name: this.content.tier3Tool4, icon: 'people', required: true }
        ]
      };

      // Assembly service handle mapping
      this.serviceHandleMapping = {
        1: 't1-assembly-service',
        2: 'riiroo-ride-on-assembly-service',
        3: 't3-assembly-service'
      };
    }

    /**
     * Initialize widget - extract tier and load data
     */
    async init() {
      // Logging removed

      // Set up PARENT product data from config (the ride-on, not the assembly service)
      this.parentProductData = {
        id: this.config.productId || null,
        tags: (this.config.productTags && this.config.productTags.length > 0) ? this.config.productTags : [],
        title: this.config.productTitle || '',
        description: this.config.productDescription || '',
        handle: this.config.productHandle || '',
        metafields: this.config.productMetafields || {},
        variants: this.config.productVariants || []
      };

      // Logging removed

      // Keep reference for backward compatibility
      this.productData = this.parentProductData;

      // Extract tier number from product tags
      this.tierNumber = this.extractTierFromTags();

      if (!this.tierNumber) {
        // Logging removed
        
        return;
      }

      // Logging removed

      // Get difficulty data for this tier
      this.difficultyData = this.difficultyMapping[this.tierNumber];

      if (!this.difficultyData) {
        console.warn('[AssemblyInfoWidget] Invalid tier number:', this.tierNumber);
        return;
      }

      // Load bundle config and find assembly addon
      this.bundleConfig = this.manager.bundleConfig;

      if (!this.bundleConfig) {
        console.warn('[AssemblyInfoWidget] Bundle config not available');
        return;
      }

      // Find assembly service addon for this tier
      await this.loadAssemblyAddon();

      // Logging removed
    }

    /**
     * Extract assembly tier number from product tags
     * Searches for tags like "Assembly-Tier-1", "Assembly-Tier-2", "Assembly-Tier-3"
     * @returns {number|null} Tier number (1, 2, or 3) or null if not found
     */
    extractTierFromTags() {
      const tags = this.productData.tags || [];

      // Priority 1: Check for explicit Assembly-Tier-1
      if (tags.some(tag => tag.match(/Assembly-Tier-1/i))) {
        return 1;
      }

      // Priority 2: Check for explicit Assembly-Tier-3
      if (tags.some(tag => tag.match(/Assembly-Tier-3/i))) {
        return 3;
      }

      // Priority 3: Check for "Product Type_Ride Ons" (defaults to Tier 2)
      if (tags.some(tag => tag.match(/Product Type_Ride Ons/i))) {
        return 2;
      }

      // No assembly tier found
      return null;
    }

    /**
     * Load assembly addon from bundle config
     */
    async loadAssemblyAddon() {
      const protectionAddons = this.bundleConfig?.addons?.protection || [];
      const serviceHandle = this.serviceHandleMapping[this.tierNumber];

      if (!serviceHandle) {
        console.warn('[AssemblyInfoWidget] No service handle for tier:', this.tierNumber);
        return;
      }

      // Find addon by handle
      this.assemblyAddon = protectionAddons.find(
        addon => addon.productHandle === serviceHandle
      );

      if (!this.assemblyAddon) {
        console.warn('[AssemblyInfoWidget] Assembly addon not found:', serviceHandle);
        return;
      }

      // Fetch actual product data from Shopify
      await this.fetchProductData();
    }

    /**
     * Fetch product data from Shopify API
     */
    async fetchProductData() {
      if (!this.assemblyAddon || !this.assemblyAddon.productHandle) {
        return;
      }

      try {
        const response = await fetch(`/products/${this.assemblyAddon.productHandle}.js`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const product = await response.json();

        // Store ASSEMBLY SERVICE product data (separate from parent product)
        this.assemblyProductData = product;

        // Get first available variant
        if (product.variants && product.variants.length > 0) {
          this.variantId = product.variants[0].id;
          this.assemblyAddon.actualPrice = product.variants[0].price;
          this.assemblyAddon.compareAtPrice = product.variants[0].compare_at_price || null;
        }

        // Logging removed

      } catch (error) {
        console.warn('[AssemblyInfoWidget] Could not fetch product data:', error);
        // Fallback to tierValue from bundle config
        this.assemblyAddon.actualPrice = this.assemblyAddon.tierValue * 100; // Convert to cents
      }
    }

    /**
     * Validate parent stock on initial render and disable buttons if unavailable
     */
    async validateParentStockOnRender() {
      const stockCheck = this.checkParentStock();

      if (!stockCheck.available) {
        console.warn('[AssemblyInfoWidget] Parent product out of stock on render:', stockCheck.message);

        // Disable "Add Assembly Service" button in modal
        const addButton = document.getElementById(`add-assembly-btn-${this.modalId}`);
        if (addButton) {
          addButton.disabled = true;
          addButton.style.opacity = '0.5';
          addButton.style.cursor = 'not-allowed';
          addButton.style.pointerEvents = 'none';
          addButton.style.backgroundColor = '#9ca3af';
          addButton.textContent = 'Parent Out of Stock';
          // Logging removed
        }

        // Show notice in modal (if modal container exists)
        const modalContent = document.querySelector(`#${this.modalId}`);
        if (modalContent) {
          const notice = document.createElement('div');
          notice.style.cssText = 'padding: 12px; margin: 16px; background: rgb(239, 68, 68, 0.1); border-radius: 8px; color: rgb(239, 68, 68); text-align: center; font-size: 14px; font-weight: 500;';
          notice.textContent = 'Parent product is currently out of stock';
          const drawerContent = modalContent.querySelector('.drawer__content');
          if (drawerContent && drawerContent.firstChild) {
            drawerContent.insertBefore(notice, drawerContent.firstChild);
          }
        }
      }
    }

    /**
     * Check if parent product has stock available
     * Validates that parent product (ride-on) has at least one variant in stock
     * @returns {{available: boolean, message: string}}
     */
    checkParentStock() {
      if (!this.parentProductData || !this.parentProductData.variants) {
        console.warn('[AssemblyInfoWidget] No parent product data available for stock check');
        return {
          available: true, // Don't block if data not available
          message: 'No parent product data available'
        };
      }

      const { variants, title } = this.parentProductData;

      // Check if ANY variant has stock available
      const hasStockInAnyVariant = variants.some(variant => {
        // Check if variant is available
        if (variant.available === true) {
          return true;
        }

        // Check inventory policy - if 'continue', can sell when out of stock
        if (variant.inventory_policy === 'continue') {
          return true;
        }

        // Check if inventory management is disabled
        if (!variant.inventory_management || variant.inventory_management === null) {
          return true;
        }

        // Check if variant has inventory quantity > 0
        if (variant.inventory_quantity && variant.inventory_quantity > 0) {
          return true;
        }

        return false;
      });

      if (hasStockInAnyVariant) {
        // Logging removed
        return {
          available: true,
          message: `Parent product "${title}" has stock available`
        };
      } else {
        // Logging removed
        return {
          available: false,
          message: `Parent product "${title}" is out of stock`
        };
      }
    }

    /**
     * Get current bundle tier discount percentage
     * @returns {number} Discount as decimal (e.g., 0.15 for 15%)
     */
    getCurrentDiscount() {
      // Check if bundleAdapter exists and has current tier info
      if (window.bundleAdapter && window.bundleAdapter.currentTier) {
        const tierKey = window.bundleAdapter.currentTier;
        const tierData = this.bundleConfig?.tiers?.[tierKey];

        if (tierData && tierData.discount) {
          // Parse discount string like "15%" to 0.15
          const discountStr = tierData.discount.replace('%', '');
          return parseFloat(discountStr) / 100;
        }
      }

      // Default: no discount
      return 0;
    }

    /**
     * Calculate discounted price
     * @param {number} basePrice - Base price in cents
     * @returns {Object} { price: number, originalPrice: number, savings: number, discount: number }
     */
    calculatePrice(basePrice) {
      const discount = this.getCurrentDiscount();
      const discountedPrice = Math.round(basePrice * (1 - discount));
      const savings = basePrice - discountedPrice;

      return {
        price: discountedPrice,
        originalPrice: basePrice,
        savings: savings,
        discount: discount
      };
    }

    /**
     * Render widget to container
     */
    render() {
      // Only show widget if we have minimum required data
      if (!this.tierNumber || !this.difficultyData) {
        // Logging removed
        this.hide();
        return;
      }

      // Build card + modal inline (x-modal pattern)
      const html = `
        ${this.buildCardHTML()}
        ${this.buildModalHTML()}
      `;

      // Use setContent() to properly handle skeleton loading
      this.setContent(html);

      // Attach event listeners
      this.attachEventListeners();

      // Check parent stock and disable buttons if unavailable (async, don't block render)
      this.validateParentStockOnRender().catch(error => {
        console.error('[AssemblyInfoWidget] Stock validation error:', error);
      });

      this.emit('assembly-info:rendered');
      // Logging removed
    }

    /**
     * Build widget card HTML
     */
    buildCardHTML() {
      const difficulty = this.difficultyData;
      const hasService = !!this.assemblyAddon;

      // Dark theme colors
      const bgColor = 'rgb(var(--color-drawer-text))';
      const textColor = 'rgb(var(--color-drawer-background))';
      const mutedColor = 'rgb(var(--color-drawer-background) / 0.7)';
      const cardBg = 'rgb(var(--color-drawer-background) / 0.1)';
      const cardBorder = 'rgb(var(--color-drawer-background) / 0.2)';

      return `
        <div class="pw-widget pw-assembly-info-widget" style="width: 100%;" data-button_hover="standard">
          <div
            class="pw-assembly-card"
            role="button"
            tabindex="0"
            aria-label="Click to learn more about assembly information"
            style="padding: 16px; background: ${bgColor}; border-radius: var(--rounded-card); cursor: pointer; transition: all 0.2s ease;">

            <!-- Header with Icon -->
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
              <div style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: ${cardBg}; border-radius: var(--rounded-button);">
                ${this.getToolsIconSVG('var(--color-drawer-background)')}
              </div>
              <div style="flex: 1;">
                <h3 class="pw-text-lg pw-font-bold" style="margin: 0; color: ${textColor};">${this.content.cardHeading}</h3>
                <p class="pw-text-sm" style="margin: 0; color: ${mutedColor};">
                  ${this.content.cardSubheadingFormat.replace('{level}', difficulty.level)}
                </p>
              </div>
            </div>

            <!-- Difficulty Badge -->
            <div style="padding: 12px; margin-bottom: 12px; background: ${cardBg}; border-radius: var(--rounded-card); border: 1px solid ${cardBorder};">
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  ${this.getWrenchRatingHTML(difficulty.stars, textColor, mutedColor)}
                  <span class="pw-text-sm pw-font-bold" style="color: ${textColor};">
                    ${difficulty.level}
                  </span>
                </div>
                <div style="display: flex; align-items: center; gap: 6px; color: ${mutedColor};">
                  ${this.getClockIconSVG(textColor)}
                  <span class="pw-text-sm">${difficulty.timeMin}-${difficulty.timeMax} mins</span>
                </div>
              </div>
            </div>

            <!-- Quick Info -->
            <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px;">
              <div style="display: flex; align-items: start; gap: 8px;">
                <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span class="pw-text-sm" style="color: ${mutedColor};">
                  ${this.getQuickDescriptionForTier()}
                </span>
              </div>
              ${hasService ? `
                <div style="display: flex; align-items: start; gap: 8px;">
                  <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: ${textColor};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="pw-text-sm" style="color: ${mutedColor};">
                    ${this.content.serviceAvailableText}
                  </span>
                </div>
              ` : ''}
            </div>

            <!-- Learn More Button -->
            <div style="display: flex; justify-content: flex-end;">
              <button
                type="button"
                class="pw-learn-more"
                aria-controls="${this.modalId}"
                aria-expanded="false"
                style="color: ${mutedColor};"
              >
                <span>Learn more</span>
                <svg class="pw-learn-more__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

          </div>
        </div>
      `;
    }

    /**
     * Get quick description text for tier
     */
    getQuickDescriptionForTier() {
      switch (this.tierNumber) {
        case 1:
          return this.content.tier1QuickDescription;
        case 2:
          return this.content.tier2QuickDescription;
        case 3:
          return this.content.tier3QuickDescription;
        default:
          return 'Assembly required';
      }
    }

    /**
     * Build modal HTML using x-modal pattern
     */
    buildModalHTML() {
      const difficulty = this.difficultyData;
      const tools = this.toolsMapping[this.tierNumber] || [];
      const hasService = !!this.assemblyAddon;

      // Calculate pricing if service available
      let pricing = null;
      if (hasService && this.assemblyAddon.actualPrice) {
        pricing = this.calculatePrice(this.assemblyAddon.actualPrice);
      } else if (hasService && this.assemblyAddon.tierValue) {
        pricing = this.calculatePrice(this.assemblyAddon.tierValue * 100);
      }

      return `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="${this.content.modalTitle}" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">${this.content.modalTitle}</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div class="text-center">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-drawer-text)); border-radius: 50%; margin-bottom: 1rem;">
                    ${this.getToolsIconSVG('var(--color-drawer-background)')}
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem; color: rgb(var(--color-drawer-text));">
                    ${this.content.modalHeroHeadingFormat.replace('{level}', difficulty.level)}
                  </h3>
                  <p class="text-base" style="max-width: 500px; margin: 0 auto; color: rgb(var(--color-drawer-text) / 0.7);">
                    ${difficulty.badge}
                  </p>
                </div>

                <!-- Difficulty Overview -->
                <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
                  <div style="display: flex; flex-direction: column; gap: 1rem;">

                    <!-- Star Rating & Level -->
                    <div style="display: flex; align-items: center; justify-content: center; gap: 12px;">
                      ${this.getStarRatingHTML(difficulty.stars, 'var(--color-drawer-text)')}
                      <span style="font-size: 1.5rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
                        ${difficulty.level}
                      </span>
                    </div>

                    <!-- Estimated Time -->
                    <div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: rgb(var(--color-drawer-text) / 0.7);">
                      ${this.getClockIconSVG()}
                      <span style="font-size: 1.125rem; font-weight: 600;">
                        ${this.content.modalTimeFormat.replace('{min}', difficulty.timeMin).replace('{max}', difficulty.timeMax)}
                      </span>
                    </div>

                  </div>
                </div>

                <!-- What to Expect -->
                ${this.buildWhatToExpectHTML()}

                <!-- Service Section (if available) -->
                ${hasService && pricing ? `
                  <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 2px solid rgb(var(--color-drawer-text) / 0.12);">
                    <h4 class="font-bold text-lg" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">
                      ${this.content.modalServiceHeading}
                    </h4>

                    <p class="text-sm" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text) / 0.85);">
                      ${this.assemblyAddon.description}
                    </p>

                    <!-- Benefits List -->
                    <div style="margin-bottom: 1.25rem;">
                      ${this.buildBenefitsHTML()}
                    </div>

                    <!-- Pricing -->
                    <div style="display: flex; flex-direction: column; gap: 0.75rem; align-items: center; margin-bottom: 1rem;">
                      <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; justify-content: center;">
                        <span style="font-size: 1.75rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
                          ${this.manager.formatPrice(pricing.price)}
                        </span>
                        ${pricing.discount > 0 ? `
                          <span style="text-decoration: line-through; color: rgb(var(--color-drawer-text) / 0.5); font-size: 1.25rem;">
                            ${this.manager.formatPrice(pricing.originalPrice)}
                          </span>
                        ` : ''}
                      </div>
                      ${pricing.discount > 0 ? `
                        <div style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; background: rgb(var(--color-drawer-text) / 0.08); border-radius: var(--rounded-button);">
                          <svg style="width: 20px; height: 20px; color: rgb(var(--color-drawer-text));" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd"/>
                          </svg>
                          <span style="font-size: 1rem; font-weight: 600; color: rgb(var(--color-drawer-text));">
                            ${this.content.modalSavingsFormat.replace('{amount}', this.manager.formatPrice(pricing.savings)).replace('{percent}', Math.round(pricing.discount * 100))}
                          </span>
                        </div>
                      ` : ''}
                    </div>

                    <!-- Add to Cart Button -->
                    <button
                      id="add-assembly-btn-${this.modalId}"
                      type="button"
                      ${!this.variantId ? 'disabled' : ''}
                      style="width: 100%; background: rgb(var(--color-base-button)); color: rgb(var(--color-base-button-text)); border: none; padding: 1rem; border-radius: var(--rounded-button); font-size: 1.125rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease; ${!this.variantId ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
                      onmouseover="${this.variantId ? 'this.style.opacity=\'0.9\'' : ''}"
                      onmouseout="${this.variantId ? 'this.style.opacity=\'1\'' : ''}">
                      ${this.variantId ? this.content.buttonAddService : this.content.buttonUnavailable}
                    </button>

                    <!-- Status Message Container -->
                    <div id="assembly-status-${this.modalId}" style="margin-top: 1rem; text-align: center; display: none;"></div>
                  </div>
                ` : ''}

                <!-- Important Notice -->
                <div style="padding: 1rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card);">
                  <div style="display: flex; align-items: start; gap: 0.75rem;">
                    ${this.getInfoIconSVG()}
                    <div style="flex: 1;">
                      <p class="text-sm" style="margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
                        ${this.content.modalNoticeText}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;
    }

    /**
     * Build supportive "What to Expect" messaging based on difficulty
     * Replaces specific tools/steps with general encouragement
     */
    buildWhatToExpectHTML() {
      let heading = '';
      let description = '';
      let highlights = [];

      switch (this.tierNumber) {
        case 1:
          heading = 'Quick & Easy Assembly';
          description = 'This ride-on has minimal assembly required. Most customers find it straightforward with basic tools.';
          highlights = [
            'Simple setup process',
            'Clear instruction manual included',
            'Most complete assembly in under an hour',
            'Perfect for DIY with basic experience'
          ];
          break;
        case 2:
          heading = 'Moderate Assembly Required';
          description = 'This ride-on requires some assembly time and patience. Many customers complete it themselves with the included instructions.';
          highlights = [
            'Step-by-step manual included',
            'Standard household tools typically sufficient',
            'Allow 1-2 hours for careful assembly',
            'Helpful to have a second person assist'
          ];
          break;
        case 3:
          heading = 'Comprehensive Assembly Required';
          description = 'This ride-on arrives partially assembled and requires significant time to complete. We strongly recommend our professional assembly service for peace of mind.';
          highlights = [
            'Detailed instruction manual provided',
            'Multiple components to assemble',
            'Allow 2-3 hours or more',
            'Professional assembly highly recommended'
          ];
          break;
        default:
          heading = 'Assembly Required';
          description = 'Some assembly is required for this product.';
          highlights = ['Instruction manual included'];
      }

      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 0.75rem; color: rgb(var(--color-drawer-text));">${heading}</h4>
          <p class="text-sm" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text) / 0.7); line-height: 1.6;">
            ${description}
          </p>
          <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
            ${highlights.map(highlight => `
              <li style="padding: 0.5rem 0; display: flex; align-items: start; gap: 0.75rem;">
                <svg style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px; color: rgb(var(--color-drawer-text));" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>${highlight}</span>
              </li>
            `).join('')}
          </ul>
        </div>
      `;
    }

    /**
     * Build assembly steps overview based on tier (DEPRECATED - replaced by buildWhatToExpectHTML)
     */
    buildAssemblyStepsHTML() {
      let steps = [];

      switch (this.tierNumber) {
        case 1:
          steps = [
            this.content.tier1Step1,
            this.content.tier1Step2,
            this.content.tier1Step3,
            this.content.tier1Step4
          ];
          break;
        case 2:
          steps = [
            this.content.tier2Step1,
            this.content.tier2Step2,
            this.content.tier2Step3,
            this.content.tier2Step4,
            this.content.tier2Step5,
            this.content.tier2Step6
          ];
          break;
        case 3:
          steps = [
            this.content.tier3Step1,
            this.content.tier3Step2,
            this.content.tier3Step3,
            this.content.tier3Step4,
            this.content.tier3Step5,
            this.content.tier3Step6,
            this.content.tier3Step7,
            this.content.tier3Step8,
            this.content.tier3Step9
          ];
          break;
      }

      return `
        <div style="padding: 1.5rem; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <h4 class="font-bold text-base" style="margin-bottom: 1rem; color: rgb(var(--color-drawer-text));">${this.content.modalStepsHeading}</h4>
          <ol class="text-sm" style="padding-left: 1.25rem; margin: 0; color: rgb(var(--color-drawer-text) / 0.85); line-height: 1.8;">
            ${steps.map(step => `<li style="padding: 0.25rem 0;">${step}</li>`).join('')}
          </ol>
        </div>
      `;
    }

    /**
     * Build benefits list for assembly service
     */
    buildBenefitsHTML() {
      const benefits = [
        this.content.benefit1,
        this.content.benefit2,
        this.content.benefit3,
        this.content.benefit4,
        this.content.benefit5
      ];

      return `
        <ul class="text-sm" style="list-style: none; padding: 0; margin: 0; color: rgb(var(--color-drawer-text) / 0.85);">
          ${benefits.map(benefit => `
            <li style="padding: 0.375rem 0; display: flex; align-items: start; gap: 0.5rem;">
              ${this.getCheckIconSVG()}
              <span>${benefit}</span>
            </li>
          `).join('')}
        </ul>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Add to cart button
      const addButton = document.getElementById(`add-assembly-btn-${this.modalId}`);
      if (addButton && this.variantId) {
        addButton.addEventListener('click', () => {
          this.addToCart();
        });
      }

      // Make assembly card clickable to open modal
      const card = this.container.querySelector('.pw-assembly-card');
      if (card) {
        // Click event
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking the learn more button
          if (e.target.closest('.pw-learn-more')) {
            return;
          }
          const modal = document.getElementById(this.modalId);
          if (modal && !modal.open) {
            modal.show();
          }
        });

        // Keyboard event (Enter or Space)
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });

        // Hover effect - add subtle grey border
        card.addEventListener('mouseenter', () => {
          card.style.outline = '2px solid rgb(var(--color-drawer-background) / 0.4)';
          card.style.outlineOffset = '-2px';
        });

        card.addEventListener('mouseleave', () => {
          card.style.outline = 'none';
        });
      }
    }

    /**
     * Add assembly service to cart
     */
    async addToCart() {
      if (!this.variantId) {
        console.warn('[AssemblyInfoWidget] No variant ID available');
        return;
      }

      const button = document.getElementById(`add-assembly-btn-${this.modalId}`);
      const statusContainer = document.getElementById(`assembly-status-${this.modalId}`);

      // Logging removed

      // Ensure ProductLinkingManager is loaded
      if (!window.ProductLinkingManager) {
        console.error('[AssemblyInfoWidget] ProductLinkingManager not loaded');
        alert(this.content.alertUnableToAdd);
        return;
      }

      // CHECK PARENT STOCK FIRST - Don't allow addon if parent is out of stock
      const parentStockCheck = this.checkParentStock();

      if (!parentStockCheck.available) {
        console.warn('[AssemblyInfoWidget] Parent product out of stock:', parentStockCheck.message);

        // Show error message
        if (statusContainer) {
          statusContainer.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68);">
              ${this.getXCircleIconSVG()}
              <span class="text-sm font-bold">Parent product is out of stock</span>
            </div>
          `;
          statusContainer.style.display = 'block';
        }

        // Disable button permanently
        if (button) {
          button.disabled = true;
          button.textContent = 'Parent Out of Stock';
        }

        return;
      }

      // Logging removed

      // Ensure PARENT product is in cart first (not the assembly service)
      // Logging removed

      const linkingManager = new ProductLinkingManager({
        productId: this.parentProductData.id,
        productHandle: this.parentProductData.handle,
        productVariantId: this.parentProductData.variants && this.parentProductData.variants[0]
          ? this.parentProductData.variants[0].id
          : null,

        // Success callback - parent is ready
        onReady: async (productLinkId, cartItem) => {
          // Logging removed

          // Disable button and show loading state
          if (button) {
            button.disabled = true;
            button.textContent = this.content.buttonAdding;
          }

          try {
            // Get tier attributes BEFORE adding (bundle drawer pattern)
            let tierAttrs = {
              _bundleTier: 'basic',
              _bundle: 'basic',
              _bundleItemCount: '1'
            };

            if (window.ProductLinkingManager && this.assemblyAddon && this.assemblyAddon.actualPrice) {
              const tempManager = new ProductLinkingManager({ productId: null, productHandle: null });
              const addonPrice = this.assemblyAddon.actualPrice / 100; // Convert from pence to pounds
              tierAttrs = await tempManager.getTierAttributesForNewAddon(productLinkId, addonPrice);
              // Logging removed
            }

            // Add assembly service with tier attributes already set (bundle drawer pattern)
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                id: this.variantId,
                quantity: 1,
                properties: {
                  productLinkId: productLinkId,
                  _bundleComponent: 'true',
                  ...tierAttrs
                }
              })
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            // Logging removed

            // Update existing addons to match the new tier/count (bundle drawer pattern)
            if (window.ProductLinkingManager) {
              const tempManager = new ProductLinkingManager({ productId: null, productHandle: null });
              await tempManager.recalculateBundleTier(productLinkId);
              // Logging removed
            }

            // Refresh cart with sections (bundle drawer pattern)
            const sectionsToUpdate = [];
            const cartDrawerElement = document.querySelector('cart-drawer');
            if (cartDrawerElement) {
              sectionsToUpdate.push(window.theme.utils.sectionId(cartDrawerElement));
            }
            const cartPageElement = document.querySelector('.cart-page');
            if (cartPageElement) {
              sectionsToUpdate.push(window.theme.utils.sectionId(cartPageElement));
            }

            const updatedCartResponse = await fetch(window.theme.routes.cart_update_url, {
              ...window.theme.utils.fetchConfig(),
              method: 'POST',
              body: JSON.stringify({ sections: sectionsToUpdate })
            });

            const updatedState = await updatedCartResponse.json();

            // Publish cart update
            if (window.theme && window.theme.pubsub) {
              window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: updatedState
              });
            }

            // Show success message
            if (statusContainer) {
              statusContainer.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(34, 197, 94, 0.1); border-radius: var(--rounded-button); color: rgb(34, 197, 94);">
                  ${this.getCheckCircleIconSVG()}
                  <span class="text-sm font-bold">Assembly service added to cart!</span>
                </div>
              `;
              statusContainer.style.display = 'block';
            }

            // Emit event
            this.emit('assembly-service:added-to-cart', {
              variantId: this.variantId,
              tier: this.tierNumber,
              product: this.assemblyAddon,
              productLinkId: productLinkId
            });

            // Update button text
            if (button) {
              button.textContent = this.content.buttonAdded;

              // Reset button after 3 seconds
              setTimeout(() => {
                button.disabled = false;
                button.textContent = this.content.buttonAddService;
                if (statusContainer) {
                  statusContainer.style.display = 'none';
                }
              }, 3000);
            }

          } catch (error) {
            console.error('[AssemblyInfoWidget] Failed to add to cart:', error);

            // Show error message
            if (statusContainer) {
              statusContainer.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px; background: rgb(239, 68, 68, 0.1); border-radius: var(--rounded-button); color: rgb(239, 68, 68);">
                  ${this.getXCircleIconSVG()}
                  <span class="text-sm font-bold">Failed to add to cart. Please try again.</span>
                </div>
              `;
              statusContainer.style.display = 'block';
            }

            // Re-enable button
            if (button) {
              button.disabled = false;
              button.textContent = 'Add Assembly Service to Cart';
            }
          }
        },

        // Cancel callback
        onCancel: () => {
          // Logging removed
        },

        // Error callback
        onError: (error) => {
          console.error('[AssemblyInfoWidget] Error ensuring parent in cart:', error);
          alert('Failed to add assembly service. Please try again.');
        }
      });

      // Trigger the parent product check
      linkingManager.ensureParentInCart();
    }

    /**
     * Cleanup
     */
    destroy() {
      // Remove modal from DOM
      const modal = document.getElementById(this.modalId);
      if (modal) {
        modal.remove();
      }

      super.destroy();
    }

    // ============================================================
    // SVG ICONS
    // ============================================================

    /**
     * Tools icon (wrench)
     */
    getToolsIconSVG(colourRGB = 'var(--color-base-button)') {
      return `
        <svg style="width: 20px; height: 20px; color: rgb(${colourRGB});" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      `;
    }

    /**
     * Clock icon (time)
     * @param {string} color - Optional color override
     */
    getClockIconSVG(color = 'rgb(var(--color-drawer-text) / 0.7)') {
      return `
        <svg style="width: 18px; height: 18px; flex-shrink: 0; color: ${color};" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Star icon (rating)
     */
    getStarIconSVG(filled = true, colourRGB = '251, 191, 36') {
      if (filled) {
        return `
          <svg style="width: 24px; height: 24px; color: rgb(${colourRGB});" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        `;
      } else {
        return `
          <svg style="width: 24px; height: 24px; color: rgb(var(--color-drawer-text) / 0.2);" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        `;
      }
    }

    /**
     * Get star rating HTML
     */
    getStarRatingHTML(count, colourRGB) {
      const maxStars = 5;
      let html = '<div style="display: flex; align-items: center; gap: 2px;">';

      for (let i = 0; i < maxStars; i++) {
        html += this.getStarIconSVG(i < count, colourRGB);
      }

      html += '</div>';
      return html;
    }

    /**
     * Generate wrench rating HTML (for dark card)
     */
    getWrenchRatingHTML(count, filledColor, emptyColor) {
      const maxWrenches = 5;
      let html = '<div style="display: flex; align-items: center; gap: 2px;">';

      for (let i = 0; i < maxWrenches; i++) {
        const color = i < count ? filledColor : emptyColor;
        html += `
          <svg style="width: 14px; height: 14px; color: ${color};" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
          </svg>
        `;
      }

      html += '</div>';
      return html;
    }

    /**
     * Check icon (benefits)
     */
    getCheckIconSVG() {
      return `
        <svg style="flex-shrink: 0; width: 18px; height: 18px; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
        </svg>
      `;
    }

    /**
     * Check circle icon (success)
     */
    getCheckCircleIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * X circle icon (error)
     */
    getXCircleIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Info icon
     */
    getInfoIconSVG() {
      return `
        <svg style="width: 20px; height: 20px; flex-shrink: 0; color: rgb(var(--color-base-button));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      `;
    }

    /**
     * Arrow right icon
     */
    getArrowRightIconSVG() {
      return `
        <svg style="width: 18px; height: 18px; color: rgb(var(--color-base-button-text));" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
        </svg>
      `;
    }

    /**
     * Get tool icon based on type
     */
    getToolIconForType(type) {
      const iconColour = 'rgb(var(--color-base-button))';

      switch (type) {
        case 'screwdriver':
          return `
            <svg style="width: 20px; height: 20px; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
            </svg>
          `;
        case 'spanner':
          return `
            <svg style="width: 20px; height: 20px; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"/>
            </svg>
          `;
        case 'drill':
          return `
            <svg style="width: 20px; height: 20px; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
            </svg>
          `;
        case 'people':
          return `
            <svg style="width: 20px; height: 20px; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
          `;
        case 'tool':
        default:
          return `
            <svg style="width: 20px; height: 20px; flex-shrink: 0; color: ${iconColour};" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          `;
      }
    }
  }

  // Register widget
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.AssemblyInfoWidget = AssemblyInfoWidget;

  // Logging removed

})(window);
